// API keys — live data from useApiKeys(). Phase 4.
//
// Security constraints (CLAUDE.md + T-4-20/T-4-21):
//   - The one-time secret lives only in the `revealed` React state from the
//     moment mintApiKey/rotateApiKey resolves until RevealKey.onClose fires
//     setRevealed(null). It is never written to browser storage or any cookie.
//   - No env picker (D-02). All keys are live only. Scopes: payouts:read,
//     payouts:write, balances:read, accounts:read.
//   - Retired and revoked keys stay visible in the table with a status pill
//     (D-03). Rotate/Revoke actions are disabled for non-active keys.
//   - Network calls go through the Plan-03 apiKeys.ts wrappers only —
//     no additional axios instances constructed here (CLAUDE.md rule 1).
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { I } from "@/components/Icons";
import { Modal, StatusPill } from "@/components/UI";
import { formatDateTime } from "@/utils/format";
import { apiErrorMessage } from "@/utils/apiError";
import { useApiKeys } from "@/queries/useApiKeys";
import { useUI } from "@/store/ui";
import { useAuth, isAdminRole } from "@/store/auth";
import { useQueryClient } from "@tanstack/react-query";
import {
  mintApiKey,
  rotateApiKey,
  revokeApiKey,
  type ApiKey,
  type ApiKeyScope,
  type MintApiKeyResult,
  type RotateApiKeyResult,
} from "@/api/apiKeys";

// API base URL for the RevealKey curl example — matches whatever host
// the apiClient is hitting (staging vs prod) so the example a fresh
// key holder copy-pastes works on the environment they minted in.
const API_BASE: string =
  (import.meta.env.VITE_PAYOUTS_API_URL as string | undefined) ||
  "https://pay.prod.khazana.cards";

// AUDIT-PASS-6 P-1: the local extractErrorCode helper was removed —
// callers now route through @/utils/apiError, which extracts the code
// from the same shape (err.response.data.code), maps known codes
// (scopes_invalid / key_not_found / key_already_revoked already live
// in ERROR_COPY) to curated copy, and provides status-aware fallback
// for unknown codes. The earlier raw "Failed to mint key: <code>"
// template leaked backend code strings to the operator; apiErrorMessage
// gives them a sentence first, code in parens after.

// ─── D-02 scope set (no balances:write, no banks:read, no webhooks:write) ────

const ALL_SCOPES: ApiKeyScope[] = [
  "payouts:read",
  "payouts:write",
  "balances:read",
  "accounts:read",
];

// ─── MintKey modal ────────────────────────────────────────────────────────────

interface MintKeyModalProps {
  open: boolean;
  onClose: () => void;
  merchantId: number;
  onMinted: (result: MintApiKeyResult) => void;
}

function MintKeyModal({
  open,
  onClose,
  merchantId,
  onMinted,
}: MintKeyModalProps) {
  const qc = useQueryClient();

  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<Record<ApiKeyScope, boolean>>({
    "payouts:read": false,
    "payouts:write": false,
    "balances:read": false,
    "accounts:read": false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);

  function resetAndClose() {
    setName("");
    setScopes({ "payouts:read": false, "payouts:write": false, "balances:read": false, "accounts:read": false });
    setSubmitting(false);
    setGeneralError(null);
    onClose();
  }

  const selectedScopes = ALL_SCOPES.filter((s) => scopes[s]);
  const canSubmit = name.trim().length > 0 && selectedScopes.length > 0 && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setGeneralError(null);
    setSubmitting(true);
    try {
      const result = await mintApiKey(merchantId, {
        name: name.trim(),
        scopes: selectedScopes,
      });
      void qc.invalidateQueries({ queryKey: ["api-keys", merchantId] });
      resetAndClose();
      onMinted(result);
    } catch (err) {
      setGeneralError(apiErrorMessage(err, "Could not mint the key. Try again."));
    } finally {
      setSubmitting(false);
    }
  }

  function toggleScope(s: ApiKeyScope) {
    setScopes((prev) => ({ ...prev, [s]: !prev[s] }));
  }

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      busy={submitting}
      title="Mint API key"
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={resetAndClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="submit"
            form="mint-key-form"
            disabled={!canSubmit}
          >
            {submitting ? "Generating…" : "Generate key"}
          </button>
        </>
      }
    >
      <form id="mint-key-form" onSubmit={handleSubmit} className="stack-16">
        <div className="field">
          <label className="label" htmlFor="mint-key-name">
            Label
          </label>
          <input
            id="mint-key-name"
            className="input"
            type="text"
            placeholder="e.g. Production · payouts service"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            required
          />
        </div>

        <div className="field">
          <label className="label">Scopes</label>
          <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
            {ALL_SCOPES.map((s) => (
              <label
                key={s}
                className="row gap-6"
                style={{
                  padding: "5px 10px",
                  border:
                    "1px solid " + (scopes[s] ? "var(--ink-2)" : "var(--hair)"),
                  borderRadius: 3,
                  fontSize: 12,
                  background: scopes[s] ? "var(--tint)" : "transparent",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!scopes[s]}
                  onChange={() => toggleScope(s)}
                />
                <span className="mono" style={{ fontSize: 11 }}>
                  {s}
                </span>
              </label>
            ))}
          </div>
          {selectedScopes.length === 0 && (
            <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
              Select at least one scope.
            </div>
          )}
        </div>

        {generalError && (
          <div style={{ color: "var(--neg)", fontSize: 12 }}>{generalError}</div>
        )}
      </form>
    </Modal>
  );
}

// ─── RevealKey modal ──────────────────────────────────────────────────────────

interface RevealKeyModalProps {
  result: MintApiKeyResult | RotateApiKeyResult | null;
  onClose: () => void;
}

function RevealKeyModal({ result, onClose }: RevealKeyModalProps) {
  const [copied, setCopied] = useState(false);

  if (!result) return null;

  const { api_key: k, secret } = result;

  function copy() {
    navigator.clipboard?.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title="Your new API key"
      wide
      disableScrimClose  /* CR-03: prevent accidental secret loss via backdrop click */
      hideCloseButton    /* WR-05: prevent premature dismissal via X button before copying */
      foot={
        <>
          <div className="flex-1" />
          <button className="btn primary" type="button" onClick={onClose}>
            I've saved this key
          </button>
        </>
      }
    >
      <div className="stack-16">
        {/* Irreversible-warning banner */}
        <div
          style={{
            background: "var(--warn-soft)",
            padding: 12,
            borderRadius: 3,
            borderLeft: "2px solid var(--warn)",
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <I.AlertCircle
            size={14}
            style={{ marginTop: 2, color: "var(--warn)" }}
          />
          <div style={{ fontSize: 12 }}>
            <span style={{ fontWeight: 500 }}>
              This is the only time you'll see this secret.
            </span>
            <span className="dim">
              {" "}
              After you close this window we will only retain a hash. If you
              lose it, you'll need to mint a new key.
            </span>
          </div>
        </div>

        {/* Secret field with copy button */}
        <div className="field">
          <label className="label">Secret key</label>
          <div className="input-group">
            <input
              className="input mono"
              readOnly
              value={secret}
              style={{ letterSpacing: "0.03em" }}
            />
            <button
              className="addon"
              type="button"
              onClick={copy}
              style={{ cursor: "pointer", padding: "0 12px", color: "var(--ink)" }}
            >
              {copied ? (
                <>
                  <I.Check size={11} stroke={2.5} /> Copied
                </>
              ) : (
                <>
                  <I.Copy size={11} /> Copy
                </>
              )}
            </button>
          </div>
        </div>

        {/* Summary — Label, Scopes, Prefix (no env row — D-02) */}
        <dl className="dl tight">
          <dt>Label</dt>
          <dd>{k.name}</dd>
          <dt>Scopes</dt>
          <dd>
            <div className="row" style={{ flexWrap: "wrap", gap: 4 }}>
              {k.scopes.map((s) => (
                <span
                  key={s}
                  className="mono"
                  style={{
                    fontSize: 11,
                    padding: "1px 6px",
                    border: "1px solid var(--hair)",
                    borderRadius: 2,
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          </dd>
          <dt>Prefix</dt>
          <dd className="mono">{k.prefix}</dd>
        </dl>

        {/* curl example */}
        <div
          className="card flat"
          style={{ border: "1px solid var(--hair)", background: "var(--bg-sunk)" }}
        >
          <div
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid var(--hair)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span className="eyebrow">Test request</span>
            <div className="flex-1" />
            <span className="mono dim" style={{ fontSize: 11 }}>
              curl
            </span>
          </div>
          <pre
            style={{
              margin: 0,
              padding: 14,
              fontFamily: "var(--font-mono)",
              fontSize: 11.5,
              color: "var(--ink-2)",
              lineHeight: 1.6,
            }}
          >
            {`curl ${API_BASE}/v1/merchants/:id/payouts \\\n  -H "Authorization: Bearer ${k.prefix}…" \\\n  -d '{"amount_minor":100,"currency":"INR"}'`}
          </pre>
        </div>
      </div>
    </Modal>
  );
}

// ─── RotateConfirm modal ──────────────────────────────────────────────────────

interface RotateConfirmProps {
  open: boolean;
  onClose: () => void;
  merchantId: number;
  apiKey: ApiKey | null;
  onRotated: (result: RotateApiKeyResult) => void;
}

function RotateConfirmModal({
  open,
  onClose,
  merchantId,
  apiKey,
  onRotated,
}: RotateConfirmProps) {
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);

  function resetAndClose() {
    setSubmitting(false);
    setGeneralError(null);
    onClose();
  }

  async function handleConfirm() {
    if (!apiKey) return;
    setGeneralError(null);
    setSubmitting(true);
    try {
      const result = await rotateApiKey(merchantId, apiKey.id);
      void qc.invalidateQueries({ queryKey: ["api-keys", merchantId] });
      resetAndClose();
      onRotated(result);
    } catch (err) {
      setGeneralError(apiErrorMessage(err, "Could not rotate the key. Try again."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      busy={submitting}
      title="Rotate API key"
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={resetAndClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? "Rotating…" : "Rotate key"}
          </button>
        </>
      }
    >
      <div className="stack-16">
        {apiKey && (
          <div
            style={{
              background: "var(--bg-sunk)",
              padding: "10px 12px",
              borderRadius: 3,
              fontSize: 12,
            }}
          >
            <span className="dim">Key: </span>
            <strong>{apiKey.name}</strong>
            <span className="dim mono" style={{ marginLeft: 8 }}>
              {apiKey.prefix}
            </span>
          </div>
        )}
        <div className="dim" style={{ fontSize: 12 }}>
          Rotating mints a replacement key and retires this one immediately
          (D-03). The current key stops working on the next request. A new
          secret will be shown once — store it in your secret manager before
          closing.
        </div>
        {generalError && (
          <div style={{ color: "var(--neg)", fontSize: 12 }}>{generalError}</div>
        )}
      </div>
    </Modal>
  );
}

// ─── RevokeConfirm modal ──────────────────────────────────────────────────────

interface RevokeConfirmProps {
  open: boolean;
  onClose: () => void;
  merchantId: number;
  apiKey: ApiKey | null;
}

function RevokeConfirmModal({
  open,
  onClose,
  merchantId,
  apiKey,
}: RevokeConfirmProps) {
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);

  function resetAndClose() {
    setSubmitting(false);
    setGeneralError(null);
    onClose();
  }

  async function handleConfirm() {
    if (!apiKey) return;
    setGeneralError(null);
    setSubmitting(true);
    try {
      await revokeApiKey(merchantId, apiKey.id);
      void qc.invalidateQueries({ queryKey: ["api-keys", merchantId] });
      resetAndClose();
    } catch (err) {
      setGeneralError(apiErrorMessage(err, "Could not revoke the key. Try again."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      busy={submitting}
      title="Revoke API key"
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={resetAndClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn danger"
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? "Revoking…" : "Revoke key"}
          </button>
        </>
      }
    >
      <div className="stack-16">
        {apiKey && (
          <div
            style={{
              background: "var(--bg-sunk)",
              padding: "10px 12px",
              borderRadius: 3,
              fontSize: 12,
            }}
          >
            <span className="dim">Key: </span>
            <strong>{apiKey.name}</strong>
            <span className="dim mono" style={{ marginLeft: 8 }}>
              {apiKey.prefix}
            </span>
          </div>
        )}
        <div className="dim" style={{ fontSize: 12 }}>
          Revoking permanently disables this key. It will remain visible in the
          table with a "Revoked" status (D-03), but will return{" "}
          <code>401 api_key_inactive</code> on the next M2M request. This
          action cannot be undone.
        </div>
        {generalError && (
          <div style={{ color: "var(--neg)", fontSize: 12 }}>{generalError}</div>
        )}
      </div>
    </Modal>
  );
}

// ─── ApiKeys page ─────────────────────────────────────────────────────────────

export function ApiKeys() {
  const merchantId = useUI((s) => s.currentMerchantId);
  const { data: keys = [], isLoading } = useApiKeys();

  // Operator+ gate. Mirrors the backend chain (RequireMembershipRole
  // operator for Mint/Rotate/Revoke; viewer for List). Admin short-circuits.
  // Viewers can still reach this page via direct URL and read the list but
  // can't mutate — the buttons are hidden rather than clickable-then-403.
  const isAdmin = useAuth((s) => isAdminRole(s.user?.role));
  const myMembership = useAuth((s) =>
    s.user?.memberships.find((m) => m.merchant_id === merchantId),
  );
  const myMembershipRole = myMembership?.role;
  // Per-merchant superadmin API toggle (backend migration 027). When
  // false the merchant's stk_… keys 401 api_disabled at BearerDispatch
  // and the key-management endpoints 403 api_disabled. Non-admin members
  // see an "API disabled" empty state here instead of the table. Admin
  // bypasses the gate so they can inspect history on disabled merchants.
  const merchantAPIEnabled = myMembership?.api_enabled ?? false;
  const apiDisabledForMember = !isAdmin && !merchantAPIEnabled;
  const canMutate =
    !apiDisabledForMember &&
    (isAdmin ||
      myMembershipRole === "owner" ||
      myMembershipRole === "operator");

  // One-time secret state — lives in memory only; dropped on RevealKey close.
  const [showMint, setShowMint] = useState(false);
  const [revealed, setRevealed] = useState<
    MintApiKeyResult | RotateApiKeyResult | null
  >(null);

  // Rotate/Revoke confirm modal state.
  const [rotateKey, setRotateKey] = useState<ApiKey | null>(null);
  const [revokeKey, setRevokeKey] = useState<ApiKey | null>(null);

  // Per-merchant API toggle off + non-admin → render an "API disabled"
  // empty state. The sidebar entry is hidden for these members already,
  // but a bookmarked /keys URL would otherwise hit a 403 toast — show a
  // clean explanation instead.
  if (apiDisabledForMember) {
    return (
      <div className="page">
        <div className="page-head">
          <div>
            <div className="eyebrow">Console · Developer</div>
            <h1>API credentials</h1>
            <div className="sub">API access is currently disabled for this merchant.</div>
          </div>
        </div>
        <div className="card">
          <div className="empty">
            <div className="icon">
              <I.Keys size={16} />
            </div>
            <h3>API access is turned off</h3>
            <p>
              Your administrator has disabled programmatic API access for
              this merchant. Existing keys (if any) will not authenticate
              until access is re-enabled. Contact your administrator to
              request access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console · Developer</div>
          <h1>API credentials</h1>
          <div className="sub">
            {isLoading
              ? "Loading…"
              : `${keys.length} key${keys.length === 1 ? "" : "s"} · live · keys are hashed at rest`}
          </div>
        </div>
        <div className="actions">
          <Link className="btn ghost" to="/keys/webhooks">
            <I.Bell size={12} /> Webhooks
          </Link>
          <Link className="btn ghost" to="/keys/docs">
            <I.Info size={12} /> API reference
          </Link>
          {merchantId != null && canMutate && (
            <button
              className="btn accent"
              type="button"
              onClick={() => setShowMint(true)}
            >
              <I.Plus size={12} /> Mint key
            </button>
          )}
        </div>
      </div>

      {/* Info banner — ported verbatim from design-reference/api-keys.jsx lines 62–68 */}
      <div
        style={{
          background: "var(--bg-sunk)",
          padding: "10px 14px",
          borderRadius: 3,
          borderLeft: "2px solid var(--accent)",
          marginBottom: 16,
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
        }}
      >
        <I.Info size={14} style={{ marginTop: 2, color: "var(--accent)" }} />
        <div style={{ fontSize: 12 }}>
          <span style={{ fontWeight: 500 }}>Secrets are shown once.</span>
          <span className="dim">
            {" "}
            Store new keys in your secret manager immediately. If lost, revoke
            and mint a new one — we cannot recover them.
          </span>
        </div>
      </div>

      <div className="card">
        {isLoading ? (
          // Skeleton table — visually distinct from the empty state below
          // (audit pass-3 C3). Matches the column shape of the data table
          // so the layout doesn't reflow when keys arrive.
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Prefix</th>
                <th>Scopes</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 4 }).map((_, i) => (
                <tr key={i}>
                  <td><span className="skel" style={{ width: 120, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 60, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 80, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 56, height: 18, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 90, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 90, height: 12, display: "inline-block" }} /></td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        ) : keys.length === 0 ? (
          <div className="empty">
            <div className="icon">
              <I.Keys size={16} />
            </div>
            <h3>No API keys minted yet</h3>
            <p>
              Programmatic access uses scoped keys. Each key is shown once at
              creation — store it in your secret manager immediately.
            </p>
            {merchantId != null && canMutate && (
              <button
                className="btn primary"
                type="button"
                onClick={() => setShowMint(true)}
              >
                <I.Plus size={12} /> Mint your first key
              </button>
            )}
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Prefix</th>
                <th>Scopes</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const isActive = k.status === "active";
                return (
                  <tr key={k.id}>
                    <td>
                      <div className="cell-name">{k.name}</div>
                      <div className="cell-id">{k.id}</div>
                    </td>
                    <td className="mono">{k.prefix}</td>
                    <td>
                      {/* Scopes display — ported from design-reference/api-keys.jsx lines 95–98 */}
                      <div className="row" style={{ flexWrap: "wrap", gap: 4 }}>
                        {k.scopes.slice(0, 3).map((s) => (
                          <span
                            key={s}
                            className="mono"
                            style={{
                              fontSize: 10.5,
                              padding: "1px 6px",
                              border: "1px solid var(--hair)",
                              borderRadius: 2,
                            }}
                          >
                            {s}
                          </span>
                        ))}
                        {k.scopes.length > 3 && (
                          <span
                            className="dim mono"
                            style={{ fontSize: 10.5 }}
                          >
                            +{k.scopes.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <StatusPill status={k.status} />
                    </td>
                    <td className="mono dim" style={{ fontSize: 11 }}>
                      {formatDateTime(k.created_at)}
                    </td>
                    <td className="mono dim" style={{ fontSize: 11 }}>
                      {k.last_used_at ? formatDateTime(k.last_used_at) : "—"}
                    </td>
                    <td className="right">
                      {canMutate ? (
                        <div className="row gap-4" style={{ justifyContent: "flex-end" }}>
                          <button
                            className="btn ghost sm"
                            type="button"
                            onClick={() => setRotateKey(k)}
                            disabled={!isActive || merchantId == null}
                            title={isActive ? "Rotate key" : "Key is not active"}
                          >
                            Rotate
                          </button>
                          <button
                            className="btn ghost sm danger"
                            type="button"
                            onClick={() => setRevokeKey(k)}
                            disabled={!isActive || merchantId == null}
                            title={isActive ? "Revoke key" : "Key is not active"}
                          >
                            Revoke
                          </button>
                        </div>
                      ) : (
                        <span className="dim" style={{ fontSize: 11 }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* MintKey modal — open only when merchantId is known */}
      {merchantId != null && (
        <MintKeyModal
          open={showMint}
          onClose={() => setShowMint(false)}
          merchantId={merchantId}
          onMinted={(result) => setRevealed(result)}
        />
      )}

      {/* RevealKey — one-time secret reveal; closing drops secret from memory */}
      <RevealKeyModal
        result={revealed}
        onClose={() => setRevealed(null)}
      />

      {/* Rotate confirm modal */}
      {merchantId != null && (
        <RotateConfirmModal
          open={rotateKey != null}
          onClose={() => setRotateKey(null)}
          merchantId={merchantId}
          apiKey={rotateKey}
          onRotated={(result) => {
            setRotateKey(null);
            setRevealed(result);
          }}
        />
      )}

      {/* Revoke confirm modal */}
      {merchantId != null && (
        <RevokeConfirmModal
          open={revokeKey != null}
          onClose={() => setRevokeKey(null)}
          merchantId={merchantId}
          apiKey={revokeKey}
        />
      )}
    </div>
  );
}
